import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Building2, ShieldAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"

import {
  crearSociedad,
  declararRegimen,
  fetchPaises,
  fetchSociedades,
  type SociedadMaestro,
} from "@/routes/masters/api"
import { SociedadFormDialog } from "@/routes/masters/SociedadFormDialog"
import { onWriteError } from "@/lib/writeErrors"
import { t } from "@/lib/i18n"

/**
 * LAS SOCIEDADES Y SU REGIMEN TRIBUTARIO.
 *
 * POR QUE ESTA PANTALLA LLEGO TARDE. El onboarding marca el paso «sociedad» en
 * ATENCION cuando falta el regimen —«el regimen decide que impuestos dan
 * credito: sin el el sistema no lo toma y lo marca para revision»— y **no habia
 * ninguna forma de darlo**: ningun endpoint escribia en `entity`. El sistema
 * pedia un dato que no se podia entregar, y tres de las cuatro sociedades
 * llevaban meses sin regimen.
 *
 * EL DESPLEGABLE SALE DEL RULEPACK, NO DE UNA LISTA DE AQUI. Un campo de texto
 * libre deja escribir «mype» donde el motor espera `MYPE_TRIBUTARIO`: el
 * onboarding se pone en verde, el calculo sigue yendo por el general y nada
 * avisa. Un pais cuyo rulepack todavia no declara sus regimenes lo DICE, en vez
 * de mostrar un desplegable vacio que parece roto.
 */
export function SociedadesPage() {
  const q = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const [alta, setAlta] = React.useState(false)
  const { toast } = useToast()
  const qc = useQueryClient()

  // Solo cuando hace falta: son los rulepacks del servidor y no cambian entre
  // sesiones, pero pedirlos al abrir la pantalla es una llamada que casi nadie
  // usa.
  const paises = useQuery({ queryKey: ["paises"], queryFn: fetchPaises, enabled: alta })

  const crear = useMutation({
    mutationFn: crearSociedad,
    onSuccess: (r) => {
      toast({
        title: `${r.razon_social} (${r.pais}) creada`,
        description: `Libros: ${r.libros_creados.join(", ")}. ${r.siguiente}`,
      })
      setAlta(false)
      void qc.invalidateQueries({ queryKey: ["sociedades"] })
      // El selector de sociedad de la barra superior y todo lo que cuelga de
      // una entidad tienen que ver la nueva sin recargar la página.
      void qc.invalidateQueries({ queryKey: ["entities"] })
      void qc.invalidateQueries({ queryKey: ["primeros-pasos"] })
      void qc.invalidateQueries({ queryKey: ["obligaciones"] })
    },
    onError: onWriteError("la sociedad", "crear"),
  })

  const boton = puedeEditar ? (
    <Button size="sm" onClick={() => setAlta(true)}>
      <Building2 />
      {t("Nueva sociedad")}</Button>
  ) : null

  return (
    <div className="space-y-3">
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && (q.data?.length ?? 0) === 0}
        emptyMessage={t("No hay sociedades en este cliente.")}
        emptyHint={t("Sin sociedad no hay libros, ni períodos, ni comprobantes: es lo primero de cualquier traspaso. Dala de alta aquí, o sube varias a la vez desde «Carga por archivo».")}
        emptyAction={boton}
        onRetry={() => void q.refetch()}
        columns={[24, 16, 10, 20, 20]}
      >
        {q.data ? (
          <Tabla sociedades={q.data} puedeEditar={puedeEditar} alta={boton} />
        ) : null}
      </DataState>

      <SociedadFormDialog
        open={alta}
        onOpenChange={setAlta}
        paises={paises.data ?? []}
        paisesCargando={paises.isLoading}
        pending={crear.isPending}
        onSubmit={async (v) => {
          // El fallo ya lo reporta `onError` con su toast, su log y su
          // referencia. Dejar que la promesa se rechace aqui lo contaria DOS
          // veces: react-hook-form la propaga y el capturador global de la SPA
          // la vuelve a mandar a /logs como si fuera un error distinto.
          await crear.mutateAsync({
            razon_social: v.razon_social,
            tax_id: v.tax_id,
            pais: v.pais,
            moneda_funcional: v.moneda_funcional,
            regimen_tributario: v.regimen_tributario || null,
            motivo: v.motivo,
          }).catch(() => undefined)
        }}
      />
    </div>
  )
}

function Tabla({
  sociedades,
  puedeEditar,
  alta,
}: {
  sociedades: SociedadMaestro[]
  puedeEditar: boolean
  alta: React.ReactNode
}) {
  const sinRegimen = sociedades.filter((s) => !s.regimen_declarado).length

  return (
    <WorkPanel
      title={t("Sociedades")}
      meta={
        sinRegimen > 0 ? (
          <Badge variant="warning-soft" estado>
            {sinRegimen}{" "}{t("sin régimen")}</Badge>
        ) : (
          <Badge variant="success-soft" estado>
            {t("todas con régimen")}</Badge>
        )
      }
      actions={alta}
      footnote={t("El régimen decide qué impuestos dan crédito, qué obligaciones aparecen y con qué escala se calcula la renta. En Brasil, PIS y COFINS solo dan crédito en Lucro Real. Cambiarlo mueve el impuesto de todos los períodos abiertos, por eso exige motivo.")}
    >
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[18rem]">{t("Sociedad")}</TableHead>
            <TableHead className="w-[10rem]">{t("Identificador fiscal")}</TableHead>
            <TableHead className="w-14">{t("País")}</TableHead>
            <TableHead className="w-20">{t("Moneda")}</TableHead>
            <TableHead>{t("Régimen tributario")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sociedades.map((s) => (
            <Fila key={s.id} s={s} puedeEditar={puedeEditar} />
          ))}
        </TableBody>
      </Table>
    </WorkPanel>
  )
}

function Fila({ s, puedeEditar }: { s: SociedadMaestro; puedeEditar: boolean }) {
  const [abierto, setAbierto] = React.useState(false)
  const [codigo, setCodigo] = React.useState(s.regimen_tributario ?? "")
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const guardar = useMutation({
    mutationFn: () =>
      declararRegimen(s.id, { regimen_tributario: codigo, motivo }),
    onSuccess: (r) => {
      toast({
        title: `${s.razon_social}: ${r.regimen_tributario}`,
        description: r.aviso ?? undefined,
      })
      setAbierto(false)
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["sociedades"] })
      // El mapa de obligaciones y los primeros pasos dependen de esto.
      void qc.invalidateQueries({ queryKey: ["obligaciones"] })
      void qc.invalidateQueries({ queryKey: ["primeros-pasos"] })
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "No se pudo declarar el régimen",
        description: e instanceof Error ? e.message : undefined,
      }),
  })

  const sinCatalogo = s.regimenes_disponibles.length === 0
  const listo = codigo.trim().length >= 2 && motivo.trim().length >= 3

  return (
    <>
      <TableRow>
        <TableCell className="truncate font-medium" title={s.razon_social}>
          {s.razon_social}
        </TableCell>
        <TableCell className="code">{s.tax_id}</TableCell>
        <TableCell className="code">{s.pais}</TableCell>
        <TableCell className="code">{s.moneda_funcional}</TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            {s.regimen_declarado ? (
              <Badge variant="outline" className="code">
                {s.regimen_tributario}
              </Badge>
            ) : (
              <span className="inline-flex items-center gap-1 text-2xs text-warning">
                <ShieldAlert className="h-3 w-3" />
                {t("Sin declarar — el cálculo va por el general")}</span>
            )}
            {puedeEditar ? (
              <Button size="xs" variant="ghost" onClick={() => setAbierto((v) => !v)}>
                {abierto ? "Cancelar" : s.regimen_declarado ? "Cambiar" : "Declarar"}
              </Button>
            ) : null}
          </div>
        </TableCell>
      </TableRow>

      {abierto ? (
        <TableRow>
          <TableCell colSpan={5} className="bg-surface-sunken">
            <div className="animate-row-in space-y-2 px-1 py-2">
              <div className="grid gap-3 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
                <div className="space-y-1">
                  <Label htmlFor={`reg-${s.id}`}>{t("Régimen")}</Label>
                  {sinCatalogo ? (
                    <>
                      {/*
                        El rulepack de este pais todavia no declara sus
                        regimenes. Se deja escribir —el dato del cliente es real
                        aunque el motor no lo modele— y se DICE, en vez de
                        mostrar un desplegable vacio.
                      */}
                      <input
                        id={`reg-${s.id}`}
                        value={codigo}
                        onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                        placeholder={t("Código del régimen")}
                        className={cn(
                          "code h-7 w-full rounded-md border bg-surface px-2 text-xs",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        )}
                      />
                      <p className="text-2xs text-warning">
                        {t("El rulepack de")}{" "}{s.pais}{" "}{t("todavía no declara sus regímenes, así que esto no se puede validar. Se guarda tal cual y el motor seguirá calculando por el general hasta que el rulepack lo modele.")}</p>
                    </>
                  ) : (
                    <select
                      id={`reg-${s.id}`}
                      value={codigo}
                      onChange={(e) => setCodigo(e.target.value)}
                      className={cn(
                        "code h-7 w-full rounded-md border bg-surface px-2 text-xs",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                    >
                      <option value="">{t("Elige un régimen…")}</option>
                      {s.regimenes_disponibles.map((r) => (
                        <option key={r.codigo} value={r.codigo}>
                          {r.codigo} · {r.nombre}
                        </option>
                      ))}
                    </select>
                  )}
                  {(() => {
                    const elegido = s.regimenes_disponibles.find((r) => r.codigo === codigo)
                    return elegido?.nota || elegido?.fundamento_legal ? (
                      <p className="text-2xs leading-4 text-muted-foreground">
                        {elegido.nota ?? elegido.fundamento_legal}
                      </p>
                    ) : null
                  })()}
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`mot-${s.id}`}>{t("Motivo *")}</Label>
                  <Textarea
                    id={`mot-${s.id}`}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    rows={2}
                    placeholder={t("Por qué. Queda en la pista de auditoría con tu nombre y la fecha.")}
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={!listo || guardar.isPending}
                  onClick={() => guardar.mutate()}
                >
                  <Building2 />
                  {guardar.isPending ? "Guardando…" : "Declarar régimen"}
                </Button>
                <span className="text-2xs text-muted-foreground">
                  {t("Cambia el impuesto de los períodos abiertos: revisa después el mapa de obligaciones.")}</span>
              </div>
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}
